<template>
  <div class="navbar-item has-dropdown is-hoverable">
    <a class="navbar-link">
        {{currentUser.firstName + ' ' + currentUser.lastName}}
    </a>
    <div class="navbar-dropdown">
      <a class="navbar-item" v-show="isAuthenticated" >
        Profile
      </a>
      <a class="navbar-item" v-show="isAuthenticated" @click="showLogout" >
        Signout
      </a>
      <a class="navbar-item" v-show="showCarfaxSessionLink" @click="showLogoutCarfax" >
        Signout Carfax
      </a>
      <a class="navbar-item" v-show="isAuthenticated && isAdmin && !isInTabletMode" @click="tabletMode" >
        Enable Tablet Mode
      </a>
      <a class="navbar-item" v-show="isAuthenticated && canAccessSecurityToken" @click="revealSecurityToken" v-tooltip="securityTokenTitle">
        {{ securityTokenDisplay }}
      </a>
      <hr class="navbar-divider">
      <div class="navbar-item">
        <span @click="onVersionClick">Version {{currentAppVersion}}</span>
      </div>
      <div class="navbar-item">
        <counter></counter>
      </div>
    </div>
    <portal to="global-modal-portal" v-if="showLogoutCarfaxWarning">
      <b-modal :active.sync="showLogoutCarfaxWarning" scroll="keep" width="640px" :has-modal-card="true" :canCancel="false">
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Carfax Logout</p>
          </header>
          <section class="modal-card-body">
            <div class="notification is-flex is-warning">
              <span><i class="fas fa-exclamation-triangle is-warning fa-2x"></i></span>
              <span class="ml-10">
                Are you sure you would like to end your carfax session?
              </span>
            </div>
          </section>
          <footer class="modal-card-foot">
            <div class="columns" style="width: 100%">
              <div class="column is-12">
                <button class="button is-primary" @click="logoutCarfax" type="button" :class="{'is-loading': logoutRequestInProgress }">Signout Carfax</button>
                <button class="button is-danger" @click="cancelCarfaxLogout" type="button" :class="{'is-loading': logoutRequestInProgress }">Cancel</button>
              </div>
            </div>
          </footer>
        </div>
      </b-modal>
    </portal>
  </div>
</template>

<script>
import util from '@/utils'
import defaultAppConfig from '@/config/index'
import counterComponent from './CounterComponent'
import appraisalService from '@/services/appraisalService'
import utilitiesMixin from '@/mixins/generic/utilities'

import { createNamespacedHelpers } from 'vuex'
const { mapState, mapGetters, mapActions } = createNamespacedHelpers('user')
const mapAuthGetters = createNamespacedHelpers('auth').mapGetters
const carfaxLogoutUrl = process.env.VUE_APP_CARFAX_LOGOUT_URL

export default {
  name: 'Profile',
  mixins: [utilitiesMixin],
  components: {
    'counter': counterComponent
  },
  data () {
    return {
      isLoading: false,
      defaultSecurityToken: '********',
      serverErrorMessage: '',
      isSecurityTokenRevealed: false,
      showLogoutCarfaxWarning: false,
      logoutRequestInProgress: false
    }
  },
  computed: {
    showProfile () {
      return true
    },
    ...mapState(['isAuthenticated', 'currentUser']),
    ...mapGetters(['allowedRoles', 'hasFeatureAccess']),
    ...mapAuthGetters(['securityToken']),

    securityTokenDisplay () {
      let securityTokenParts = this.securityToken.split(',')
      return this.isSecurityTokenRevealed ? securityTokenParts[0] : (this.isLoading ? 'Loading...' : this.defaultSecurityToken)
    },
    securityTokenTitle () {
      let securityTokenParts = this.securityToken.split(',')
      return this.isSecurityTokenRevealed ? '<b>PREVIOUS:</b> ' + securityTokenParts[1] + '<br/>' + '<b>CURRENT:</b> ' + securityTokenParts[0] : (this.isLoading ? 'Loading...' : this.defaultSecurityToken)
    },
    availableRolesOther () {
      return this.$store.getters['user/allowedRoles']
    },
    canAccessSecurityToken: function () {
      return this.hasFeatureAccess('view.security.token')
    },
    isAdmin () {
      return this.$store.getters['user/isInRole']('Admin Role')
    },
    currentAppVersion: function () {
      return defaultAppConfig.appVersion
    },
    isInTabletMode: function () {
      return util.isInTabletMode()
    },
    showCarfaxSessionLink: function () {
      if (this.currentUser && this.currentUser.carFaxSession && this.currentUser.carFaxSession.hasValidSession) {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions(['toggleDesign']),
    revealSecurityToken: function () {
      let that = this
      if (this.isSecurityTokenRevealed) return
      this.$store.dispatch('auth/revealSecurityToken', { subscriberId: this.currentUser.subscriberId })
      that.isSecurityTokenRevealed = true
      setTimeout(function () {
        that.isSecurityTokenRevealed = false
      }, 60000)
    },
    onVersionClick: function () {
      this.toggleDesign()
    },
    showLogout: function () {
      this.$store.dispatch('auth/logoutRequest')
    },
    showLogoutCarfax: function () {
      this.showLogoutCarfaxWarning = true
    },
    cancelCarfaxLogout: function () {
      this.showLogoutCarfaxWarning = false
    },
    logoutCarfax: function () {
      this.logoutRequestInProgress = true
      let that = this

      appraisalService.logoutCarfaxSession(this.currentUser.id).then(response => {
        that.logoutRequestInProgress = false
        if (response.errors) {
          that.failedToast('Error occurred when trying to logout of carfax session.')
        } else {
          //update current user carfax session          
          that.$store.dispatch('user/setUserCarfaxSession', null).then((x) => {
            that.showLogoutCarfaxWarning = false
            const parsedUrl = new URL(carfaxLogoutUrl)
            if (parsedUrl.protocol === 'https:' && parsedUrl.hostname.endsWith('carfax.com') ) {
              const carfaxWindow = window.open(carfaxLogoutUrl, '_blank')
              if (!carfaxWindow) {
                that.warningToast('Server session cleared but browser session could not be cleared because popup was blocked. In future please disable popup blocker for complete logout.')
              } else {
                that.successToast('Your carfax session has ended.')
              }
            } else {
              that.successToast('Your carfax session has ended.')
            }
          }).catch((error) => {
            that.failedToast('Error occurred when trying to logout of carfax session.')
          })
        }
      }).catch((error) => {
        that.logoutRequestInProgress = false
        that.failedToast('Error occurred when trying to logout of carfax session.')
      })
    },
    tabletMode: function () {
      this.$store.dispatch('auth/tabletMode')
    }
  }
}
</script>
